import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

export function PersistedPage(): Node {
  return html`
    <div>
      <h1>Persisted Signals</h1>
      <p><code>createPersisted</code> creates a signal that automatically syncs with <code>localStorage</code>. The value persists across page refreshes and browser sessions.</p>

      <h2>Basic Usage</h2>
      ${CodeBlock(`import { createPersisted } from 'onefold/persist';

// Persists under key 'user-theme' in localStorage
const theme = createPersisted('user-theme', 'light');

theme()       // 'light' (or stored value if previously set)
theme.set('dark');  // updates signal AND localStorage`)}

      <h2>With Objects</h2>
      ${CodeBlock(`interface Preferences {
  fontSize: number;
  sidebarOpen: boolean;
  locale: string;
}

const prefs = createPersisted<Preferences>('app-prefs', {
  fontSize: 14,
  sidebarOpen: true,
  locale: 'en',
});

// Objects are serialized as JSON
prefs.set({ ...prefs(), fontSize: 16 });`)}

      <h2>Options</h2>
      ${CodeBlock(`const token = createPersisted('auth-token', '', {
  storage: sessionStorage,   // use sessionStorage instead
  serialize: (v) => btoa(v), // custom serializer
  deserialize: (s) => atob(s), // custom deserializer
});`)}

      <table>
        <tr><th>Option</th><th>Type</th><th>Default</th><th>Description</th></tr>
        <tr><td><code>storage</code></td><td>Storage</td><td>localStorage</td><td>Storage backend (localStorage, sessionStorage).</td></tr>
        <tr><td><code>serialize</code></td><td>(value) => string</td><td>JSON.stringify</td><td>Custom serializer for storage.</td></tr>
        <tr><td><code>deserialize</code></td><td>(raw) => T</td><td>JSON.parse</td><td>Custom deserializer from storage.</td></tr>
      </table>

      ${Callout('If localStorage is unavailable (e.g., incognito mode in some browsers), createPersisted falls back to an in-memory signal.')}

      <h2>Reactive in Templates</h2>
      ${CodeBlock(`function SettingsPanel(): Node {
  const fontSize = createPersisted('font-size', 14);

  return html\`
    <div>
      <p>Font size: \${() => fontSize()}px</p>
      <button onclick=\${() => fontSize.set(s => s + 1)}>Increase</button>
      <button onclick=\${() => fontSize.set(s => s - 1)}>Decrease</button>
      <button onclick=\${() => fontSize.set(14)}>Reset</button>
    </div>
  \`;
}`)}

      ${Playground(`import { createSignal, html, mount } from 'onefold';
import { createPersisted } from 'onefold/persist';

function App() {
  // createPersisted: signal that auto-saves to localStorage
  const fontSize = createPersisted('demo-font-size', 14);
  const username = createPersisted('demo-username', '');
  const darkMode = createPersisted('demo-dark', false);

  return html\`
    <div style=\${() => darkMode() ? 'background:#1e293b;color:#e2e8f0;padding:16px;border-radius:8px' : 'padding:16px'}>
      <h3>Persisted Settings</h3>
      <p style="font-size:12px;color:\${() => darkMode() ? '#94a3b8' : '#666'};margin-bottom:16px">
        These values persist in localStorage. Edit them, then click "Simulate Reload" to see them restored.
      </p>

      <div style="margin-bottom:12px">
        <label style="font-size:13px;font-weight:600">Username</label>
        <div style="margin-top:4px">
          <input
            placeholder="Enter your name..."
            value=\${() => username()}
            oninput=\${(e) => username.set(e.target.value)}
            style="width:100%"
          />
        </div>
      </div>

      <div style="margin-bottom:12px">
        <label style="font-size:13px;font-weight:600">Font Size: \${() => fontSize()}px</label>
        <div style="display:flex;gap:8px;margin-top:4px">
          <button onclick=\${() => fontSize.set(s => s - 1)}>−</button>
          <button onclick=\${() => fontSize.set(s => s + 1)}>+</button>
          <button onclick=\${() => fontSize.set(14)}>Reset</button>
        </div>
      </div>

      <div style="margin-bottom:16px">
        <label style="font-size:13px;font-weight:600">Dark Mode</label>
        <div style="margin-top:4px">
          <button onclick=\${() => darkMode.set(d => !d)}>
            \${() => darkMode() ? '☀️ Switch to Light' : '🌙 Switch to Dark'}
          </button>
        </div>
      </div>

      <div style="padding:12px;border-radius:6px;border:1px solid \${() => darkMode() ? '#334155' : '#e5e7eb'}">
        <p style=\${() => 'font-size:' + fontSize() + 'px'}>
          \${() => username() ? 'Hello, ' + username() + '!' : 'Hello, stranger!'}
        </p>
        <p style="font-size:11px;margin-top:4px;opacity:0.6">
          This text size responds to the font-size setting above.
        </p>
      </div>

      <div style="display:flex;gap:8px;margin-top:12px">
        <button onclick=\${() => { fontSize.clear(); username.clear(); darkMode.clear(); }}>
          Clear All (Reset Storage)
        </button>
      </div>

      <p style="font-size:11px;margin-top:12px;opacity:0.6">
        localStorage keys: demo-font-size, demo-username, demo-dark
      </p>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'createPersisted — Settings that Survive Reload', { allowStorage: true })}

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/state/store">Store</a> — manage structured application state</li>
        <li><a href="/theming">Theming</a> — reactive CSS custom properties with theme switching</li>
      </ul>
    </div>
  `;
}
